import { FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useAuthenticateInviteCode } from '@vs-online-only-app/state';
import SplitInput from './Splitinput';
import { CreateAccountContext } from '../../CreateAccountContext';

const INVITE_CODE_LENGTH = 7;

const EnterInviteCode: FC = () => {
  const { goToNextStep, inviteCode, setInviteCode } =
    useContext(CreateAccountContext);

  const defaultArray = inviteCode
    ? inviteCode.split('')
    : (Array.from({ length: INVITE_CODE_LENGTH }).fill('') as string[]);

  const [valueArray, setValueArray] = useState<string[]>(defaultArray);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const authenticateInviteCode = useAuthenticateInviteCode();

  const newInviteCode = valueArray.join('');
  const isCodeValid = newInviteCode.length === INVITE_CODE_LENGTH;

  const handleSubmit = useCallback(async () => {
    if (!isCodeValid) return;

    setIsLoading(true);

    try {
      const { data } = await authenticateInviteCode.mutateAsync(newInviteCode);
      if (!data.valid) throw new Error('Invalid invite code');
      goToNextStep();
      setInviteCode(newInviteCode);
    } catch {
      setError(true);
    }

    setIsLoading(false);
  }, [
    authenticateInviteCode,
    goToNextStep,
    isCodeValid,
    newInviteCode,
    setInviteCode,
  ]);

  const handleChange = (newValueArray: string[]) => {
    setError(false);
    setValueArray(newValueArray);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isCodeValid) {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCodeValid, handleSubmit]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ width: '-webkit-fill-available' }}>
          <AlertTitle>Incorrect invite code</AlertTitle>Sorry, we don't
          recognize the code that you entered. Please check and try again.
        </Alert>
      )}
      <Stack gap={2} alignItems="center">
        <SplitInput valueArray={valueArray} onChange={handleChange} />
        <MuiLink
          onClick={() => setIsModalOpen(true)}
          sx={{ cursor: 'pointer' }}
        >
          <Typography variant="body2" color="textSecondary">
            What is this?
          </Typography>
        </MuiLink>
        <Dialog open={isModalOpen} onClose={closeModal}>
          <DialogTitle>What is this?</DialogTitle>
          <Divider />
          <DialogContent>
            <DialogContentText>
              Your invite code is a unique 7 digit code. If you don't have one,
              ask your manager.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>OK</Button>
          </DialogActions>
        </Dialog>
      </Stack>
      <Button
        onClick={handleSubmit}
        fullWidth
        size="large"
        variant="contained"
        disabled={!isCodeValid || isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Next'}
      </Button>
    </>
  );
};

export default EnterInviteCode;
